import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, ShoppingBag, CheckCircle2, Loader2 } from 'lucide-react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

export default function LandingPage() {
  useEffect(() => {
    const id = 'cormorant-garamond-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const go = (path) => () => { window.location.href = path; };

  // Detect order success / mailing-list-join URL params
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const orderSuccess = params.get('order') === 'success';
  const sessionId = params.get('session_id') || '';
  const joinMode = params.get('join') === 'true';
  const prefillEmail = params.get('email') || '';
  const joinAuthorId = params.get('authorId') || '';
  const joinAuthorName = params.get('authorName') || '';
  const joinBookId = params.get('bookId') || '';
  const joinBookTitle = params.get('bookTitle') || '';
  const alreadyOptedIn = params.get('optedIn') === '1';

  if (orderSuccess || joinMode) {
    return (
      <ThankYouPage
        serif={serif}
        sessionId={sessionId}
        prefillEmail={prefillEmail}
        joinMode={joinMode}
        joinAuthorId={joinAuthorId}
        joinAuthorName={joinAuthorName}
        joinBookId={joinBookId}
        joinBookTitle={joinBookTitle}
        alreadyOptedIn={alreadyOptedIn}
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0d1117] to-slate-950 text-white flex flex-col"
      style={serif}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <div className="w-full max-w-3xl text-center">
          <h1
            className="text-6xl sm:text-7xl md:text-8xl font-semibold tracking-wide bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent drop-shadow-[0_2px_12px_rgba(217,165,75,0.25)]"
            style={serif}
          >
            NARRATORIA
          </h1>
          <p className="mt-4 text-lg sm:text-xl italic text-slate-300 tracking-wide" style={serif}>
            Where stories find their readers
          </p>
          <div className="mx-auto mt-8 flex items-center justify-center gap-3">
            <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-amber-500/60" />
            <span className="h-1.5 w-1.5 rotate-45 bg-amber-400/80" />
            <span className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-amber-500/60" />
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            <button
              onClick={go('/reader/select-author')}
              className="group relative overflow-hidden rounded-2xl border border-teal-500/40 bg-gradient-to-br from-teal-600/20 via-slate-900/60 to-slate-900/80 p-8 text-left shadow-xl shadow-teal-900/30 transition-all hover:border-teal-400 hover:shadow-teal-500/30 hover:-translate-y-0.5"
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-900/40">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-white" style={serif}>Enter the Portal</h2>
              <p className="mt-2 text-sm text-slate-300 font-sans">
                Request ARCs, access your books, manage your account
              </p>
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-teal-400 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>

            <button
              onClick={go('/reader/shop')}
              className="group relative overflow-hidden rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-600/15 via-slate-900/60 to-slate-900/80 p-8 text-left shadow-xl shadow-amber-900/20 transition-all hover:border-amber-400 hover:shadow-amber-500/30 hover:-translate-y-0.5"
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-900/40">
                <ShoppingBag className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-white" style={serif}>Buy a Book</h2>
              <p className="mt-2 text-sm text-slate-300 font-sans">
                Browse and purchase books directly — no account needed
              </p>
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80 py-6 px-4 text-center">
        <p className="text-xs text-slate-500 font-sans">
          Are you an author?{' '}
          <a href="mailto:admin@narratoria.com?subject=Author%20access%20request" className="text-teal-500 hover:text-teal-400">
            Apply to publish on Narratoria
          </a>
        </p>
      </div>
    </div>
  );
}

function ThankYouPage({ serif, sessionId, prefillEmail, joinMode, joinAuthorId, joinAuthorName, joinBookId, joinBookTitle, alreadyOptedIn }) {
  const [email, setEmail] = useState(prefillEmail);
  const [joining, setJoining] = useState(false);
  // If arriving via the email "join" link, assume they haven't opted in yet.
  // If arriving via order-success, the webhook will already have recorded opt-in
  // for anyone who ticked the box — but we can't know that client-side, so we
  // always offer the link and let the server dedupe.
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState('');

  async function joinList() {
    const clean = (email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setJoinError('Please enter a valid email address.');
      return;
    }
    if (!joinAuthorId || !joinBookId) {
      setJoinError('This link is missing author details. Please return to the shop and sign up from a book page.');
      return;
    }
    setJoining(true);
    setJoinError('');
    try {
      const fn = httpsCallable(functions, 'joinMailingList');
      await fn({
        authorId: joinAuthorId,
        authorName: joinAuthorName || '',
        bookId: joinBookId,
        bookTitle: joinBookTitle || '',
        name: clean.split('@')[0],
        email: clean,
      });
      setJoined(true);
    } catch (err) {
      setJoinError(err.message || 'Could not join the mailing list right now.');
    } finally {
      setJoining(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0d1117] to-slate-950 text-white flex flex-col"
      style={serif}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <div className="w-full max-w-2xl text-center">
          {!joinMode && (
            <>
              <div className="mx-auto mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full border-2 border-amber-400/70 bg-gradient-to-br from-amber-500/30 to-amber-700/20 shadow-xl shadow-amber-900/40">
                <CheckCircle2 className="h-10 w-10 text-amber-300" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-wide bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent" style={serif}>
                Thank you for your order!
              </h1>
              <p className="mt-4 text-base sm:text-lg italic text-slate-300" style={serif}>
                Your order has been confirmed and you'll receive a confirmation email shortly.
              </p>
              {sessionId && (
                <p className="mt-2 text-xs text-slate-500 font-sans">
                  Order reference: {sessionId.slice(-10)}
                </p>
              )}
            </>
          )}
          {joinMode && (
            <>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-wide bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent" style={serif}>
                Welcome to Narratoria
              </h1>
              <p className="mt-4 text-base sm:text-lg italic text-slate-300" style={serif}>
                Join the mailing list for early access and author news.
              </p>
            </>
          )}

          <div className="mx-auto mt-10 flex items-center justify-center gap-3">
            <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-amber-500/60" />
            <span className="h-1.5 w-1.5 rotate-45 bg-amber-400/80" />
            <span className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-amber-500/60" />
          </div>

          <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-900/10 via-slate-900/60 to-slate-900/80 p-6 sm:p-8 text-left shadow-xl shadow-amber-900/20">
            {alreadyOptedIn && !joinMode && !joined ? (
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-amber-300" />
                <p className="mt-3 text-lg text-amber-200" style={serif}>You're already on the list!</p>
                <p className="mt-1 text-sm text-slate-400 font-sans">Thanks for signing up during checkout.</p>
              </div>
            ) : joined ? (
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-amber-300" />
                <p className="mt-3 text-lg text-amber-200" style={serif}>You're on the list!</p>
                <p className="mt-1 text-sm text-slate-400 font-sans">Thank you — keep an eye on your inbox.</p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-white" style={serif}>
                  {joinMode ? 'One-click mailing list signup' : 'Want to hear more from Julieanna Mac?'}
                </h2>
                <p className="mt-2 text-sm text-slate-300 font-sans leading-relaxed">
                  Join the mailing list for early access to future books, audiobook releases,
                  exclusive content and author news.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setJoinError(''); }}
                    placeholder="you@example.com"
                    className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none font-sans"
                  />
                  <button
                    onClick={joinList}
                    disabled={joining || !email}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-amber-400 to-amber-600 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-900/30 hover:from-amber-300 hover:to-amber-500 disabled:opacity-60 disabled:cursor-not-allowed font-sans"
                  >
                    {joining ? <><Loader2 className="h-4 w-4 animate-spin" /> Joining…</> : 'Yes, add me to the mailing list'}
                  </button>
                </div>
                {joinError && <p className="mt-3 text-xs text-rose-400 font-sans">{joinError}</p>}
              </>
            )}
          </div>

          <button
            onClick={() => { window.location.href = '/reader'; }}
            className="mt-10 inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/70 px-5 py-2.5 text-sm text-slate-200 hover:bg-slate-800 font-sans"
          >
            Back to Narratoria
          </button>
        </div>
      </div>
    </div>
  );
}
